import React, { useEffect, useState, useCallback } from 'react';
import Sidebanner from './sidebanner';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase';
import CustomerReview from '../../components/customerReviews';

export default function SubmissionHistory() {
  const { business, loading: authLoading } = useAuth();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ⚡️ Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 20;

  const fetchPendingReviews = useCallback(async () => {
    if (!business) return;

    try {
      setLoading(true);
      
      // Calculate range for Supabase
      const from = currentPage * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data, error, count } = await supabase
        .from('reviews')
        .select('*', { count: 'exact' }) // Get total count for pagination math
        .eq('submission_type', 'bwt')
        .eq('is_approved', false)
        .in('status', ['pending_audit', 'declined'])
        .eq('company', business.www)
        .order('created_at', { ascending: false })
        .range(from, to); // ⚡️ Fetch only the 20 items for this page

      if (error) throw error;
      
      setPendingReviews(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching submissions:', error.message);
    } finally {
      setLoading(false);
    }
  }, [business, currentPage]);

  useEffect(() => {
    fetchPendingReviews();
    // Scroll to top when page changes for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchPendingReviews]);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className='min-h-screen bg-[#f9f9f9] flex'>
      <div className='flex-1 px-8 lg:px-16'>
        <h1 className='text-[32px] lg:text-[60px] font-staat font-semibold xs:pt-[100px] pt-[80px] text-[#32596E] mb-10 uppercase italic'>
          Submission History
        </h1>

        {loading || authLoading ? (
          <p className="text-gray-400 animate-pulse font-light tracking-widest text-xs uppercase">
            Synchronizing System Ledger...
          </p>
        ) : pendingReviews.length > 0 ? (
          <>
            <div className='flex flex-col gap-3 pb-10 max-w-[850px]'>
              {pendingReviews.map((review) => (
                <div key={review.id} className='flex flex-col mb-12'>
                  
                  {/* Status & Reason Row */}
                  <div className={`flex items-start max-w-[370px] xs:max-w-[450px] 2xl:max-w-[600px] w-full mx-auto gap-3 mb-3 font-bold text-[9px] tracking-[0.15em] uppercase ${
                    review.status === 'pending_audit' ? 'text-[#32596E]' : 'text-red-600'
                  }`}>
                    <div className="flex-shrink-0 mt-[2px]">
                      <svg width="8" height="8" viewBox="0 0 8 8" className={review.status === 'pending_audit' ? 'animate-pulse' : ''}>
                        <circle cx="4" cy="4" r="3.5" fill="currentColor" />
                      </svg>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span>{review.status === 'pending_audit' ? 'System Audit Pending' : 'Review Declined'}</span>
                      {review.status === 'declined' && (
                        <p className="normal-case font-medium text-[10px] leading-relaxed opacity-95 max-w-[500px]">
                          <span className="mr-1 font-black">REASON:</span> 
                          <span className="italic text-red-700">{review.Decline_Reason || 'Manual Audit Rejection'}</span>  
                        </p>
                      )}
                    </div>
                  </div>

                  <CustomerReview 
                    author={review.author}
                    title={review.title}
                    text={review.text}
                    submission_type={'bwt'}
                    stars={review.stars}
                    date={new Date(review.created_at).toLocaleDateString()}
                    isPending={true}
                  />

                  {review.evidence_url && (
                    <div className='mt-4 ml-0 lg:ml-6'>
                      <div className='relative w-full max-w-[400px] h-[220px] bg-gray-50 rounded border border-gray-200 overflow-hidden group'>
                        <img 
                          src={review.evidence_url} 
                          alt="Evidence" 
                          className="w-full h-full object-contain cursor-zoom-in opacity-90 transition-opacity hover:opacity-100"
                          onClick={() => window.open(review.evidence_url, '_blank')}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-8 w-full h-[1px] bg-gray-100"></div>
                </div>
              ))}
            </div>

            {/* ⚡️ Pagination Controls */}
            {totalPages >= 1 && (
              <div className="flex items-center  gap-4 pb-20 max-w-[850px] justify-center ">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="px-6 py-2 bg-white border border-gray-200 text-[10px] font-black uppercase tracking-widest disabled:opacity-20 hover:bg-gray-50 transition-colors"
                >
                  PREVIOUS
                </button>
                
                <span className="text-[10px] font-bold text-[#32596E] uppercase tracking-widest">
                  Page {currentPage + 1} of {totalPages}
                </span>

                <button 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage === totalPages - 1}
                  className="px-6 py-2 bg-white border border-gray-200 text-[10px] font-black uppercase tracking-widest disabled:opacity-20 hover:bg-gray-50 transition-colors"
                >
                  NEXT
                </button>
              </div>
            )}
          </>
        ) : (
          <div className='pt-10'>
            <p className='text-gray-400 font-light italic'>The history ledger is currently empty.</p>
          </div>
        )}
      </div>

      <div className="hidden lg:block w-[350px]">
        <Sidebanner />
      </div>
    </div>
  );
}